<div class="tab-content user-management">
  <div>
    Add, invite, and manage users in your organization. Each user is assigned roles directly
    @if (accountService.rbacFullEnabled) {
      <span> or through groups</span>
    }
    , which define what they can view or change in Arpio
  </div>
  <div class="flex justify-between margin-top">
    <div class="flex items-center">
      <h2>All Users</h2>
      @if (canManageUsers$ | async) {
        <a
          class="underline margin-left"
          (click)="openInviteUsersModal()"
          data-cy="invite-users-button"
          [class.loading]="openingInviteModal"
        >
          @if (openingInviteModal) {
            <i class="fa-solid fa-spinner fa-spin text-sm"></i>
          }
          @if (!openingInviteModal) {
            <i class="fa-utility fa-semibold fa-plus text-sm"></i>
          }
          Invite New Users
        </a>
      }
    </div>
    <div class="flex items-center gap-4 user-management-filter-inputs-container half-margin-bottom">
      <!-- Role Filter Dropdown - Hidden when rbacEnabled is false -->
      @if (rbacEnabled) {
        <div class="filter-container">
          <div class="flex items-center gap-4">
            @if (selectedRoleFilters.length > 0 || selectedGroupFilters.length > 0 || selectedIdpFilters.length > 0) {
              <div class="filter-count">
                ({{ selectedRoleFilters.length + selectedGroupFilters.length + selectedIdpFilters.length }}
                <span
                  >filter
                  @if (selectedRoleFilters.length + selectedGroupFilters.length + selectedIdpFilters.length > 1) {
                    <span>s</span>
                  }
                  applied.)</span
                >
                @if (
                  selectedRoleFilters.length > 0 || selectedGroupFilters.length > 0 || selectedIdpFilters.length > 0
                ) {
                  <button class="clear-filters-btn" (click)="clearRoleFilters()">Clear All</button>
                }
              </div>
            }
            <div class="filter-by-container" #filterDropdown>
              <button class="filter-by-button" (click)="toggleRoleFilterDropdown()">
                Filter by...
                <i class="fa-utility fa-semibold fa-angle-down"></i>
              </button>
              @if (showRoleFilterDropdown) {
                <div class="filter-by-dropdown">
                  <!-- Identity Providers Section -->
                  <div class="filter-section">
                    <h3 class="filter-section-header" (click)="toggleFilterSection('identityProviders')">
                      <i
                        class="fa-utility fa-semibold"
                        [class.fa-angle-down]="filterSections.identityProviders"
                        [class.fa-angle-right]="!filterSections.identityProviders"
                      ></i>
                      Identity Providers
                    </h3>
                    <div class="filter-by-options" [class.collapsed]="!filterSections.identityProviders">
                      @for (idp of getAvailableIdentityProviders(); track idp) {
                        <div class="filter-option">
                          <input
                            type="checkbox"
                            class="arpio-checkbox"
                            [id]="'idp-filter-' + idp"
                            [checked]="selectedIdpFilters.includes(idp)"
                            (change)="onIdpFilterChange(idp, $any($event.target).checked)"
                          />
                          <label [for]="'idp-filter-' + idp">{{ idp }}</label>
                        </div>
                      }
                    </div>
                  </div>
                  <!-- Roles Section -->
                  <div class="filter-section">
                    <h3 class="filter-section-header" (click)="toggleFilterSection('roles')">
                      <i
                        class="fa-utility fa-semibold"
                        [class.fa-angle-down]="filterSections.roles"
                        [class.fa-angle-right]="!filterSections.roles"
                      ></i>
                      Roles
                    </h3>
                    <div class="filter-by-options" [class.collapsed]="!filterSections.roles">
                      @for (role of getAvailableRoles(); track role) {
                        <div class="filter-option">
                          <input
                            type="checkbox"
                            class="arpio-checkbox"
                            [id]="'role-filter-' + role"
                            [checked]="selectedRoleFilters.includes(role)"
                            (change)="onRoleFilterChange(role, $any($event.target).checked)"
                          />
                          <label [for]="'role-filter-' + role">{{ role }}</label>
                        </div>
                      }
                    </div>
                  </div>
                  <!-- Groups Section -->
                  <div class="filter-section">
                    <h3 class="filter-section-header" (click)="toggleFilterSection('groups')">
                      <i
                        class="fa-utility fa-semibold"
                        [class.fa-angle-down]="filterSections.groups"
                        [class.fa-angle-right]="!filterSections.groups"
                      ></i>
                      Groups
                    </h3>
                    <div class="filter-by-options" [class.collapsed]="!filterSections.groups">
                      @for (group of getAvailableGroups(); track group) {
                        <div class="filter-option">
                          <input
                            type="checkbox"
                            class="arpio-checkbox"
                            [id]="'group-filter-' + group"
                            [checked]="selectedGroupFilters.includes(group)"
                            (change)="onGroupFilterChange(group, $any($event.target).checked)"
                          />
                          <label [for]="'group-filter-' + group">{{ group }}</label>
                        </div>
                      }
                    </div>
                  </div>
                </div>
              }
            </div>
          </div>
        </div>
      }
      <div class="filter-container">
        <input pInputText #filterInput type="text" (input)="filterGlobal($event)" placeholder="Search..." />
        @if (!isFilterApplied && !loadingFilterInput) {
          <div class="fa-utility-fill fa-semibold fa-filter fade-icon"></div>
        }
        @if (isFilterApplied && !loadingFilterInput) {
          <div (click)="clearInput()" class="fa-utility fa-semibold fa-xmark"></div>
        }
        @if (loadingFilterInput) {
          <div class="fa-solid fa-spinner fa-spin"></div>
        }
      </div>
    </div>
  </div>
  <!-- Unauthorized State -->
  @if ((canViewUsers$ | async) === false) {
    <div class="unauthorized-container" data-cy="unauthorized-message">
      <div class="unauthorized-content">
        <h3>Access Restricted</h3>
        <p>{{ unauthorizedMessage$ | async }}</p>
      </div>
    </div>
  }

  <!-- Authorized Content -->
  @if ((canViewUsers$ | async) === true && users != null) {
    <div class="table-container">
      @if (users.length > 0) {
        <div class="table-section main-table">
          <p-table
            #dt1
            [value]="filteredUsers"
            [globalFilterFields]="globalFilterFields"
            dataKey="accountSubjectId"
            data-cy="user-table"
            [scrollable]="true"
            scrollHeight="60vh"
            class="p-datatable-sm arp-prime-datatable"
            [rowHover]="true"
            [totalRecords]="users?.length ?? 0"
            [paginator]="false"
            [rowTrackBy]="userTrackBy"
            selectionMode="single"
            [(selection)]="selectedTableItem"
            (onRowSelect)="onRowSelect($event.data)"
          >
            <ng-template #header>
              <tr>
                <th pSortableColumn="name" class="strong-text name-th">
                  Name <p-sort-icon field="name"></p-sort-icon>
                </th>
                <th pSortableColumn="email" class="strong-text email-th">
                  Email <p-sort-icon field="email"></p-sort-icon>
                </th>
                @if (rbacEnabled) {
                  <th pSortableColumn="roles" class="strong-text roles-th">
                    Roles <p-sort-icon field="roles"></p-sort-icon>
                  </th>
                }
                @if (rbacEnabled) {
                  <th pSortableColumn="groups" class="strong-text groups-th">
                    Groups <p-sort-icon field="groups"></p-sort-icon>
                  </th>
                }
                <th pSortableColumn="identityProviderName" class="strong-text idp-th">
                  Identity Provider <p-sort-icon field="identityProviderName"></p-sort-icon>
                </th>
                <th pSortableColumn="lastActive" class="strong-text last-active-th">
                  Last Active <p-sort-icon field="lastActive"></p-sort-icon>
                </th>
              </tr>
            </ng-template>
            <ng-template #body let-user let-index="rowIndex">
              <tr
                [pSelectableRow]="user"
                [ngClass]="{
                  'selected-row':
                    showSidePanel && selectedUser && selectedUser.accountSubjectId === user.accountSubjectId,
                  'default-role-row': isLoggedInUser(user),
                  'refreshing-row': isUserRefreshing(user),
                }"
                data-cy="user-row"
              >
                <td class="name-column" style="position: relative">
                  <div class="user-info">
                    <div class="rbac-table-display-container">
                      @if (isUserRefreshing(user)) {
                        <i class="fa-solid fa-spinner fa-spin half-margin-right"></i>
                      }
                      <span>{{ user.name + (isLoggedInUser(user) ? ' (You)' : '') }}</span>
                    </div>
                  </div>
                </td>
                <td class="email-column">
                  <div class="rbac-table-display-container">
                    <span>{{ user.email }}</span>
                  </div>
                </td>
                @if (rbacEnabled) {
                  <td class="roles-column">
                    <div class="roles-list">
                      @if (user.roles.length === 0 && user.ssoRoles.length === 0) {
                        <span>
                          <em>None</em>
                        </span>
                      }
                      <!-- Manual roles -->
                      @for (role of user.roles | slice: 0 : 2; track role) {
                        <a
                          [routerLink]="['/' + accountService.accountId + '/settings/role-management']"
                          [queryParams]="{ selectRole: role.name }"
                          class="role-tag-link"
                        >
                          <p-tag severity="info" class="role-tag clickable-tag" value="{{ role.name }}"> </p-tag>
                        </a>
                      }
                      <!-- SSO roles (with icon) -->
                      @for (role of user.ssoRoles | slice: 0 : Math.max(0, 2 - user.roles.length); track role) {
                        <a
                          [routerLink]="['/' + accountService.accountId + '/settings/role-management']"
                          [queryParams]="{ selectRole: role.name }"
                          class="role-tag-link sso-role"
                        >
                          <p-tag severity="info" class="role-tag clickable-tag sso-tag">
                            <i class="fa-utility fa-semibold fa-id-card sso-icon"></i>
                            <span>{{ role.name }}</span>
                          </p-tag>
                        </a>
                      }
                      @if (user.roles.length + user.ssoRoles.length > 2) {
                        <span
                          class="more-count"
                          [pTooltip]="getAdditionalRolesTooltip(user)"
                          tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                          tooltipPosition="top"
                          [autoHide]="false"
                          [hideDelay]="200"
                          [escape]="false"
                          tooltipEvent="hover"
                        >
                          +{{ user.roles.length + user.ssoRoles.length - 2 }} more
                        </span>
                      }
                    </div>
                  </td>
                }
                @if (rbacEnabled) {
                  <td class="groups-column">
                    <div class="groups-list">
                      @if (user.groups.length === 0 && user.ssoGroups.length === 0) {
                        <span>
                          <em>None</em>
                        </span>
                      }
                      <!-- Manual groups -->
                      @for (group of user.groups | slice: 0 : 2; track group) {
                        <a
                          [routerLink]="['/' + accountService.accountId + '/settings/group-management']"
                          [queryParams]="{ selectGroup: group.name }"
                          class="group-tag-link"
                        >
                          <p-tag severity="info" class="group-tag clickable-tag" value="{{ group.name }}"> </p-tag>
                        </a>
                      }
                      <!-- SSO groups (with icon) -->
                      @for (group of user.ssoGroups | slice: 0 : Math.max(0, 2 - user.groups.length); track group) {
                        <a
                          [routerLink]="['/' + accountService.accountId + '/settings/group-management']"
                          [queryParams]="{ selectGroup: group.name }"
                          class="group-tag-link sso-group"
                        >
                          <p-tag severity="info" class="group-tag clickable-tag sso-tag">
                            <i class="fa-utility fa-semibold fa-id-card sso-icon"></i>
                            <span>{{ group.name }}</span>
                          </p-tag>
                        </a>
                      }
                      @if (user.groups.length + user.ssoGroups.length > 2) {
                        <span
                          class="more-count"
                          [pTooltip]="getAdditionalGroupsTooltip(user)"
                          tooltipStyleClass="custom-scrollable-tooltip badge-tooltip"
                          tooltipPosition="top"
                          [autoHide]="false"
                          [hideDelay]="200"
                          [escape]="false"
                          tooltipEvent="hover"
                        >
                          +{{ user.groups.length + user.ssoGroups.length - 2 }} more
                        </span>
                      }
                    </div>
                  </td>
                }
                <td class="idp-column">
                  {{ user.identityProviderName }}
                </td>
                <td class="last-active-column">
                  <div class="rbac-table-display-container">
                    <span
                      [pTooltip]="getLastActiveTooltip(user.lastActive)"
                      tooltipStyleClass="arp-tooltip"
                      tooltipPosition="top"
                      [autoHide]="false"
                      [hideDelay]="200"
                      tooltipEvent="hover"
                      >{{ getLastActiveDisplay(user.lastActive) }}</span
                    >
                  </div>
                </td>
              </tr>
            </ng-template>
            <ng-template #emptymessage>
              <tr>
                <td [attr.colspan]="rbacEnabled ? 6 : 4" class="empty-state-cell text-center">
                  <div class="empty-state-content">
                    <p class="empty-state-message">No results found</p>
                    <p class="empty-state-submessage">Try adjusting your search or filter criteria.</p>
                  </div>
                </td>
              </tr>
            </ng-template>
            <ng-template #summary>
              <div class="flex align-items-center justify-content-between">
                <span
                  >Displaying {{ dt1.filteredValue?.length ?? dt1.value?.length }} of
                  {{ dt1.value?.length }} users</span
                >
              </div>
            </ng-template>
          </p-table>
        </div>
      } @else {
        <!-- Empty State -->
        <div class="empty-state-container">
          <div class="empty-state-content">
            <h3>No users have been created yet.</h3>
            <p>Users can be assigned to roles or groups within your arpio application.</p>
          </div>
        </div>
      }
      <!-- Side Panel -->
      <div class="side-panel" [ngClass]="{ open: showSidePanel }" #sidePanel data-cy="user-sidebar">
        <div class="side-panel-header">
          <div class="flex items-center">
            <button class="close-btn" (click)="closeSidePanel()">
              <i class="fa-utility fa-semibold fa-angle-right"></i>
            </button>
            <h3>User Details</h3>
          </div>
          <div>
            @if (rbacEnabled && selectedUser && (canManageUsers$ | async)) {
              <button class="edit-button" (click)="openEditUserModal()">
                <i class="fa-utility fa-semibold fa-pen-to-square"></i>
              </button>
            }
          </div>
        </div>
        <div class="side-panel-content">
          <!-- Basic Information Section -->
          <div class="detail-section">
            <div class="detail-item">
              <label>Name:</label>
              @if (showSidePanel && selectedUser) {
                <span>{{ selectedUser.name }}</span>
              }
              @if (!showSidePanel || !selectedUser) {
                <div class="detail-skeleton"></div>
              }
            </div>
            <div class="detail-item">
              <label>Email:</label>
              @if (showSidePanel && selectedUser) {
                <span>{{ selectedUser.email }}</span>
              }
              @if (!showSidePanel || !selectedUser) {
                <div class="detail-skeleton"></div>
              }
            </div>
            @if (!isNativeArpioUser(selectedUser)) {
              <div class="detail-item">
                <label></label><em class="text-right">This user is associated with an external Identity Provider</em>
              </div>
            }
          </div>
          <!-- Assigned Roles Section -->
          @if (rbacEnabled) {
            <div class="detail-section">
              @if (showSidePanel && selectedUser) {
                <h4>Assigned Roles ({{ getUserRoles().length + (selectedUser?.ssoRoles?.length || 0) }})</h4>
              }
              <div class="detail-item display-block">
                @if (
                  showSidePanel &&
                  selectedUser &&
                  (getUserRoles().length > 0 || (selectedUser.ssoRoles && selectedUser.ssoRoles.length > 0))
                ) {
                  <div class="roles-list">
                    <!-- Manual roles -->
                    @for (role of getUserRoles(); track role) {
                      <a
                        [routerLink]="['/' + accountService.accountId + '/settings/role-management']"
                        [queryParams]="{ selectRole: role.name }"
                        class="role-tag-link"
                      >
                        <p-tag severity="info" class="role-tag clickable-tag">
                          {{ role.name }}
                        </p-tag>
                      </a>
                    }
                    <!-- SSO roles -->
                    @for (role of selectedUser.ssoRoles; track role) {
                      <a
                        [routerLink]="['/' + accountService.accountId + '/settings/role-management']"
                        [queryParams]="{ selectRole: role.name }"
                        class="role-tag-link sso-role"
                      >
                        <p-tag severity="info" class="role-tag clickable-tag sso-tag">
                          <i class="fa-utility fa-semibold fa-id-card sso-icon"></i>
                          <span>{{ role.name }}</span>
                        </p-tag>
                      </a>
                    }
                  </div>
                }
                @if (
                  showSidePanel &&
                  selectedUser &&
                  getUserRoles().length === 0 &&
                  (!selectedUser.ssoRoles || selectedUser.ssoRoles.length === 0) &&
                  isNativeArpioUser(selectedUser)
                ) {
                  <div class="no-data-text">No roles assigned</div>
                }
                @if (!showSidePanel || !selectedUser) {
                  <div class="detail-skeleton"></div>
                }
              </div>
            </div>
          }
          <!-- Assigned Groups Section -->
          @if (rbacEnabled) {
            <div class="detail-section">
              @if (showSidePanel && selectedUser) {
                <h4>Assigned Groups ({{ getUserGroups().length + (selectedUser?.ssoGroups?.length || 0) }})</h4>
              }
              <div class="detail-item display-block">
                @if (
                  showSidePanel &&
                  selectedUser &&
                  (getUserGroups().length > 0 || (selectedUser.ssoGroups && selectedUser.ssoGroups.length > 0))
                ) {
                  <div class="groups-list">
                    <!-- Manual groups -->
                    @for (group of getUserGroups(); track group) {
                      <a
                        [routerLink]="['/' + accountService.accountId + '/settings/group-management']"
                        [queryParams]="{ selectGroup: group.name }"
                        class="group-tag clickable-tag"
                      >
                        {{ group.name }}
                      </a>
                    }
                    <!-- SSO groups -->
                    @for (group of selectedUser.ssoGroups; track group) {
                      <a
                        [routerLink]="['/' + accountService.accountId + '/settings/group-management']"
                        [queryParams]="{ selectGroup: group.name }"
                        class="group-tag-link sso-group"
                      >
                        <p-tag severity="info" class="group-tag clickable-tag sso-tag">
                          <i class="fa-utility fa-semibold fa-id-card sso-icon"></i>
                          <span>{{ group.name }}</span>
                        </p-tag>
                      </a>
                    }
                  </div>
                }
                @if (
                  showSidePanel &&
                  selectedUser &&
                  getUserGroups().length === 0 &&
                  (!selectedUser.ssoGroups || selectedUser.ssoGroups.length === 0) &&
                  isNativeArpioUser(selectedUser)
                ) {
                  <div class="no-data-text">No groups assigned</div>
                }
                @if (!showSidePanel || !selectedUser) {
                  <div class="detail-skeleton"></div>
                }
              </div>
            </div>
          }
          <!-- Additional Information Section -->
          @if (selectedUser?.subject || !showSidePanel) {
            <div class="detail-section">
              <h4>Additional Information</h4>
              @if (selectedUser?.subject?.identityProviderId || !showSidePanel) {
                <div class="detail-item">
                  <label>Identity Provider:</label>
                  @if (showSidePanel && selectedUser?.subject?.identityProviderId) {
                    <span>{{ getIdentityProviderName(selectedUser) }}</span>
                  }
                  @if (!showSidePanel || !selectedUser?.subject?.identityProviderId) {
                    <div class="detail-skeleton"></div>
                  }
                </div>
              }
              <div class="detail-item">
                <label>Status:</label>
                <div class="flex-row items-center gap-2">
                  @if (canResendVerificationEmail()) {
                    <a class="resend-verification-button" (click)="resendVerificationEmail()">
                      <i class="fa-utility fa-semibold fa-envelope"></i>
                      Resend Email
                    </a>
                  }
                  @if (showSidePanel && selectedUser) {
                    <p-tag
                      [value]="selectedUser.verified ? 'Verified' : 'Unverified'"
                      [severity]="selectedUser.verified ? 'success' : 'warn'"
                      [icon]="
                        !selectedUser.verified ? 'fa-utility fa-semibold fa-circle-exclamation warning-icon' : null
                      "
                      [title]="!selectedUser.verified ? 'Email not verified' : null"
                    ></p-tag>
                  }
                </div>
                @if (!showSidePanel || !selectedUser) {
                  <div class="tag-skeleton"></div>
                }
              </div>
              <div class="detail-item">
                <label>Last Login:</label>
                @if (showSidePanel && selectedUser) {
                  <span [innerHTML]="getLastActiveSidePanelDisplay(selectedUser.lastActive)"></span>
                }
                @if (!showSidePanel || !selectedUser) {
                  <div class="detail-skeleton"></div>
                }
              </div>
              @if (selectedUser?.subject?.createdAt || !showSidePanel) {
                <div class="detail-item">
                  <label>Created At:</label>
                  @if (showSidePanel && selectedUser?.subject?.createdAt) {
                    <span>{{ selectedUser.subject.createdAt | date: 'medium' }}</span>
                  }
                  @if (!showSidePanel || !selectedUser?.subject?.createdAt) {
                    <div class="detail-skeleton"></div>
                  }
                </div>
              }
            </div>
          }
          <!-- Action Buttons Section -->
          @if (showSidePanel && selectedUser && (canResendInviteEmail() || canDeleteUser())) {
            <div class="detail-section action-buttons" data-cy="actions-section">
              <h4>Actions</h4>
              <div class="flex-row items-center gap-2 margin-top">
                <!-- Resend Invite Button-->
                @if (canResendInviteEmail()) {
                  <div class="margin-auto text-center margin-top">
                    <a
                      class="resend-invite-button text-center"
                      (click)="resendInviteEmail()"
                      data-cy="resend-invite-button"
                    >
                      <i class="fa-utility fa-semibold fa-envelope"></i>
                      Resend Invite Email
                    </a>
                  </div>
                }
                <!-- Delete Button -->
                <div class="margin-auto text-center margin-top">
                  @if (canDeleteUser()) {
                    <a class="delete-user-button text-center" (click)="deleteUser()" data-cy="delete-user-button">
                      Delete User...
                    </a>
                  }
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  } @else {
    <i class="fa-solid fa-spinner fa-spin"></i> Loading users...
  }

  <app-invite-users-modal #inviteUsersModal (usersInvited)="onUsersInvited()"></app-invite-users-modal>
  <app-edit-user-modal #editUserModal (userUpdated)="onUserUpdated($event)"></app-edit-user-modal>

  <!-- Delete User Confirmation Modal -->
  <modal-dialog #deleteUserDialog [dialogTemplate]="confirmDeleteUserDialog"></modal-dialog>
  <ng-template #confirmDeleteUserDialog>
    <confirm-action-dialog
      name="&quot;{{ userToDelete?.name }}&quot; ({{ userToDelete?.email }})"
      action="Delete"
      [error]="deleteUserDialog.error"
      [busy]="deletingUser"
      (onCancel)="cancelDeleteUser()"
      (onAction)="confirmDeleteUser()"
    >
      <div class="error margin-top">
        Deleting this user will permanently remove them from the account and revoke all their access. This action cannot
        be undone.
      </div>
    </confirm-action-dialog>
  </ng-template>
</div>
